import { useCallback, useEffect, useState } from 'react';
import {
  knowledgeApi,
  type ActivityEntry,
  type ContextPackage,
  type KnowledgeDashboard,
  type KnowledgeNode,
  type KnowledgeRecommendation,
  type KnowledgeRelationship,
  type MemoryObject,
  type QualityMetrics,
  type SearchResult,
} from '../../api/knowledge';

type Tab = 'overview' | 'graph' | 'search' | 'memory' | 'context' | 'quality' | 'timeline';

export function KnowledgePlatformPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<KnowledgeDashboard | null>(null);
  const [nodes, setNodes] = useState<KnowledgeNode[]>([]);
  const [relationships, setRelationships] = useState<KnowledgeRelationship[]>([]);
  const [memories, setMemories] = useState<MemoryObject[]>([]);
  const [quality, setQuality] = useState<QualityMetrics | null>(null);
  const [recommendations, setRecommendations] = useState<KnowledgeRecommendation[]>([]);
  const [searchQuery, setSearchQuery] = useState('trail runner listing');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [contextPkg, setContextPkg] = useState<ContextPackage | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, n, rels, mem, q, recs, act] = await Promise.all([
        knowledgeApi.dashboard(),
        knowledgeApi.listNodes(),
        knowledgeApi.listRelationships(),
        knowledgeApi.listMemories(),
        knowledgeApi.quality(),
        knowledgeApi.recommendations(),
        knowledgeApi.listActivity(),
      ]);
      setDashboard(dash);
      setNodes(n);
      setRelationships(rels);
      setMemories(mem);
      setQuality(q);
      setRecommendations(recs);
      setActivity(act);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load knowledge platform');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const runSearch = async () => {
    setBusy(true);
    try {
      const results = await knowledgeApi.search({ query: searchQuery, mode: 'hybrid', limit: 10 });
      setSearchResults(results);
    } finally {
      setBusy(false);
    }
  };

  const assembleContext = async () => {
    setBusy(true);
    try {
      const pkg = await knowledgeApi.buildContext({ query: searchQuery, request_id: `req-${Date.now()}` });
      setContextPkg(pkg);
      setTab('context');
    } finally {
      setBusy(false);
    }
  };

  const storeSampleMemory = async () => {
    setBusy(true);
    try {
      await knowledgeApi.storeMemory({
        kind: 'successful_prompt',
        content: 'Use benefit-driven titles with primary keyword in first 60 characters',
        outcome: 'CTR +12%',
      });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'graph', label: 'Knowledge Graph' },
    { id: 'search', label: 'Semantic Search' },
    { id: 'memory', label: 'AI Memory' },
    { id: 'context', label: 'Context Inspector' },
    { id: 'quality', label: 'Quality Dashboard' },
    { id: 'timeline', label: 'Timeline' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Knowledge Platform</h1>
          <p className="text-sm text-slate-500">
            Enterprise knowledge graph, semantic search, AI memory &amp; RAG — FCP-00013
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              tab === t.id ? 'bg-indigo-100 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Knowledge Nodes', value: dashboard.total_nodes },
            { label: 'Relationships', value: dashboard.total_relationships },
            { label: 'Documents', value: dashboard.total_documents },
            { label: 'AI Memories', value: dashboard.total_memories },
            { label: 'Embeddings', value: dashboard.embeddings_indexed },
            { label: 'Searches (24h)', value: dashboard.searches_24h },
            { label: 'Context Packages (24h)', value: dashboard.context_packages_24h },
            { label: 'Avg Retrieval', value: `${dashboard.avg_retrieval_ms}ms` },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'graph' && (
        <div className="space-y-4">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {nodes.map((n) => (
                  <tr key={n.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium">{n.name}</td>
                    <td className="px-4 py-3">{n.type}</td>
                    <td className="px-4 py-3 text-xs">{n.source}</td>
                    <td className="px-4 py-3">{(n.confidence * 100).toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-900">Relationships ({relationships.length})</h3>
            <ul className="mt-2 space-y-1 text-xs text-slate-600">
              {relationships.map((r) => (
                <li key={r.id}>
                  {r.from_node_id} → {r.to_node_id} ({r.type})
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'search' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3 rounded-xl border border-slate-200 bg-white p-4">
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="Natural language or keyword search..."
            />
            <button
              type="button"
              onClick={runSearch}
              disabled={busy}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Search
            </button>
            <button
              type="button"
              onClick={assembleContext}
              disabled={busy}
              className="rounded-lg border border-indigo-600 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50"
            >
              Build RAG Context
            </button>
          </div>
          <div className="space-y-2">
            {searchResults.map((r) => (
              <div key={r.id} className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-900">{r.title}</span>
                  <span className="text-xs text-indigo-600">score {(r.score * 100).toFixed(0)}%</span>
                </div>
                <p className="mt-1 text-sm text-slate-600">{r.snippet}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'memory' && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={storeSampleMemory}
            disabled={busy}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Store Sample Memory
          </button>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Kind</th>
                  <th className="px-4 py-3">Content</th>
                  <th className="px-4 py-3">Outcome</th>
                  <th className="px-4 py-3">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {memories.map((m) => (
                  <tr key={m.id} className="border-t border-slate-100">
                    <td className="px-4 py-3">{m.kind}</td>
                    <td className="px-4 py-3 max-w-md truncate">{m.content}</td>
                    <td className="px-4 py-3">{m.outcome || '—'}</td>
                    <td className="px-4 py-3">{(m.confidence * 100).toFixed(0)}%</td>
                  </tr>
                ))}
                {memories.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-slate-500">No memories stored yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'context' && contextPkg && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Context Package</h3>
            <span className="text-sm text-indigo-600">Score {(contextPkg.score * 100).toFixed(0)}%</span>
          </div>
          <p className="text-sm text-slate-600">{contextPkg.explanation}</p>
          <p className="text-xs text-slate-500">Token estimate: {contextPkg.token_estimate}</p>
          {contextPkg.rules_applied && (
            <div>
              <p className="text-xs font-medium text-slate-500">Rules Applied</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {contextPkg.rules_applied.map((r) => (
                  <span key={r} className="rounded bg-slate-100 px-2 py-0.5 text-xs">{r}</span>
                ))}
              </div>
            </div>
          )}
          {contextPkg.nodes && contextPkg.nodes.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500">Retrieved Nodes</p>
              <ul className="mt-1 text-sm text-slate-700">
                {contextPkg.nodes.map((n) => (
                  <li key={n.id}>{n.name} ({n.type})</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {tab === 'context' && !contextPkg && (
        <p className="text-sm text-slate-500">Run a search and click &quot;Build RAG Context&quot; to inspect assembled context.</p>
      )}

      {tab === 'quality' && quality && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: 'Freshness', value: quality.freshness },
              { label: 'Confidence', value: quality.confidence },
              { label: 'Completeness', value: quality.completeness },
              { label: 'Consistency', value: quality.consistency },
              { label: 'Coverage', value: quality.coverage },
              { label: 'Conflicts', value: quality.conflicts },
            ].map((m) => (
              <div key={m.label} className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-xs text-slate-500">{m.label}</p>
                <p className="mt-1 text-xl font-semibold">
                  {typeof m.value === 'number' && m.label !== 'Conflicts'
                    ? `${(m.value * 100).toFixed(0)}%`
                    : m.value}
                </p>
              </div>
            ))}
          </div>
          {recommendations.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-900">Recommendations</h3>
              <ul className="mt-2 space-y-2">
                {recommendations.map((r) => (
                  <li key={r.id} className="text-sm">
                    <span className="font-medium">{r.title}</span>
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">{r.priority}</span>
                    <p className="text-slate-600">{r.description}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {tab === 'timeline' && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900">Knowledge Activity</h3>
          <ul className="mt-2 space-y-2 text-sm text-slate-600">
            {activity.map((a) => (
              <li key={a.id}>
                <span className="font-medium">{a.action}</span> — {a.message}
                <span className="ml-2 text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
